// -----------------------------------------------------------------------------
// WebSocket Service - Ghi Connect Frontend
// -----------------------------------------------------------------------------
// Service for real-time chat updates via WebSocket
// -----------------------------------------------------------------------------

type MessageHandler = (data: WebSocketMessage) => void;

export interface WebSocketMessage {
  type:
    | 'new_message'
    | 'lead_updated'
    | 'connection_status'
    | 'lead_assigned'
    | 'subscribed'
    | 'unsubscribed'
    | 'pong'
    | 'disparo_executed'
    | 'campaign_item_updated';
  leadId?: string;
  data?: unknown;
  timestamp?: string;
}

export interface NewMessageData {
  mensagemId: string;
  conteudo: string;
  tipo: string;
  direcao: 'enviada' | 'recebida';
  criadoEm: string;
  /** Epoch ms from WhatsApp (not DB insert time) */
  mensagemTimestamp?: number;
  mediaUrl?: string;
  s3Key?: string;
}

export interface ConnectionStatusData {
  accountId: string;
  accountName: string;
  provider: 'evolution' | 'meta';
  status: 'conectado' | 'desconectado' | 'erro' | 'aguardando_qr';
  updatedAt: string;
  ultimaConexaoAt?: string;
}

class WebSocketService {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private pingInterval: NodeJS.Timeout | null = null;
  private listeners: Map<string, Set<MessageHandler>> = new Map();
  private currentLeadId: string | null = null;
  private token: string | null = null;
  private isConnecting = false;

  // Initialize WebSocket connection
  connect(token: string): void {
    if (this.isConnecting || (this.ws && this.ws.readyState === WebSocket.OPEN)) {
      console.log('WebSocket already connected or connecting');
      return;
    }

    this.token = token;
    this.isConnecting = true;

    const wsUrl = process.env.REACT_APP_WS_URL;
    if (!wsUrl) {
      console.warn('WebSocket URL not configured (REACT_APP_WS_URL)');
      this.isConnecting = false;
      return;
    }

    console.log('Connecting to WebSocket:', wsUrl);

    try {
      this.ws = new WebSocket(`${wsUrl}?token=${token}`);

      this.ws.onopen = () => {
        console.log('WebSocket connected');
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.emit('connection_status', { type: 'connection_status', data: 'connected' });

        // Start ping interval to keep connection alive
        this.startPingInterval();

        // Re-subscribe to current lead if any
        if (this.currentLeadId) {
          this.subscribeToLead(this.currentLeadId);
        }
      };

      this.ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          console.log('WebSocket message received:', message);
          this.emit(message.type, message);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      this.ws.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason);
        this.isConnecting = false;
        this.stopPingInterval();
        this.emit('connection_status', { type: 'connection_status', data: 'disconnected' });
        this.scheduleReconnect();
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        this.isConnecting = false;
      };
    } catch (error) {
      console.error('Error creating WebSocket:', error);
      this.isConnecting = false;
      this.scheduleReconnect();
    }
  }

  // Disconnect WebSocket
  disconnect(): void {
    this.stopPingInterval();
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.currentLeadId = null;
    this.token = null;
    this.reconnectAttempts = 0;
  }

  // Subscribe to messages for a specific lead
  subscribeToLead(leadId: string): void {
    this.currentLeadId = leadId;

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ action: 'subscribe', leadId }));
      console.log('Subscribed to lead:', leadId);
    } else {
      console.log('WebSocket not ready, will subscribe when connected');
    }
  }

  // Unsubscribe from current lead
  unsubscribeFromLead(): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN && this.currentLeadId) {
      this.ws.send(JSON.stringify({ action: 'unsubscribe' }));
      console.log('Unsubscribed from lead');
    }
    this.currentLeadId = null;
  }

  // Add event listener
  on(event: string, handler: MessageHandler): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(handler);
  }

  // Remove event listener
  off(event: string, handler: MessageHandler): void {
    const handlers = this.listeners.get(event);
    if (handlers) {
      handlers.delete(handler);
    }
  }

  // Emit event to all listeners
  private emit(event: string, message: WebSocketMessage): void {
    const handlers = this.listeners.get(event);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(message);
        } catch (error) {
          console.error('Error in WebSocket handler:', error);
        }
      });
    }
  }

  // Schedule reconnection with exponential backoff
  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log('Max reconnection attempts reached');
      return;
    }

    if (!this.token) {
      console.log('No token available, skipping reconnect');
      return;
    }

    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
    console.log(`Scheduling reconnect in ${delay}ms (attempt ${this.reconnectAttempts + 1})`);

    this.reconnectTimeout = setTimeout(() => {
      this.reconnectAttempts++;
      this.connect(this.token!);
    }, delay);
  }

  // Start ping interval to keep connection alive
  private startPingInterval(): void {
    this.stopPingInterval();
    this.pingInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ action: 'ping' }));
      }
    }, 30000); // Ping every 30 seconds
  }

  // Stop ping interval
  private stopPingInterval(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  // Check if connected
  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  // Get current subscribed lead
  getCurrentLeadId(): string | null {
    return this.currentLeadId;
  }
}

// Export singleton instance
export const wsService = new WebSocketService();
